import { handleReturn, MutationFunction, PaginatedResponse, QueryFunction } from './root'
import { network } from '@root/utils'

export interface IReactFlowViewport {
  x: number
  y: number
  zoom: number
}

export interface IReactFlowNode {
  id: string
  type?: string
  position: { x: number; y: number }
  data: Record<string, unknown> & { config?: Record<string, unknown> }
  measured?: { width?: number; height?: number }
  selected?: boolean
}

export interface IReactFlowEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
}

export interface IReactFlowData {
  nodes: IReactFlowNode[]
  edges: IReactFlowEdge[]
  viewport?: IReactFlowViewport
}

export interface IWorkflow {
  id: number
  name: string
  react_flow_data: IReactFlowData
  enabled: boolean
  latest_saved_version_number: number | null
  created_at: string
  updated_at: string
}

export type WorkflowRunStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled'

export type WorkflowNodeRunStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'cancelled'

export interface IWorkflowRun {
  id: number
  created_at: string
  updated_at: string
  version_number: number
  status: WorkflowRunStatus
  trigger_node_uuid: string
  event_id: number | null
  started_at: string | null
  finished_at: string | null
  last_error: string | null
}

export interface IWorkflowNodeRun {
  id: number
  created_at: string
  updated_at: string
  node_uuid: string
  node_type: string
  status: WorkflowNodeRunStatus
  reason: string | null
  error: string | null
  started_at: string | null
  finished_at: string | null
  output_items: { kind: 'document' | 'file'; ids: number[] }[] | null
}

export interface IWorkflowRunDetail extends IWorkflowRun {
  react_flow_data: IReactFlowData
  node_runs: Record<string, IWorkflowNodeRun>
}

interface WorkflowListProps {
  slug: string
}

interface WorkflowInstanceProps {
  slug: string
  workflowId: number
}

interface WorkflowRunDetailProps extends WorkflowInstanceProps {
  runId: number
}

export interface IWorkflowNodeTypeOption {
  value: string
  label: string
}

export interface IWorkflowEventTriggerTypeOption {
  value: string
  label: string
}

export interface IWorkflowScheduledTriggerFrequencyOption {
  value: string
  label: string
}

export interface IScheduledTriggerPreview {
  timezone: string
  next_runs: string[]
}

export interface IScheduledTriggerPreviewRequest {
  frequency: string
  hour: number
  minute: number
  day_of_week?: number | null
  day_of_month?: number | null
}

export interface IWorkflowFilterOperatorOption {
  value: string
  label: string
  sentence_template?: string
  relative_sentence_template?: string
}

export interface IWorkflowFilterChoiceOption {
  value: boolean
  label: string
  sentence_template?: string
}

export interface IWorkflowFilterFieldOption {
  value: string
  label: string
  type: 'datetime' | 'boolean' | 'text' | 'account' | 'contact' | 'amount'
  operators: IWorkflowFilterOperatorOption[]
  choices: IWorkflowFilterChoiceOption[]
}

export interface IWorkflowFilterTypeOption {
  value: string
  label: string
  sentence_lead?: string
  fields: IWorkflowFilterFieldOption[]
}

export const fetchWorkflowEventTriggerTypes: QueryFunction<
  WorkflowListProps,
  IWorkflowEventTriggerTypeOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/workflow_event_trigger_types/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchWorkflowScheduledTriggerFrequencies: QueryFunction<
  WorkflowListProps,
  IWorkflowScheduledTriggerFrequencyOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/workflow_scheduled_trigger_frequencies/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

/** POST-shaped read: the schedule config is the query, so it cannot go in a query string. */
export const fetchScheduledTriggerPreview = async (
  { slug }: WorkflowListProps,
  requestData: IScheduledTriggerPreviewRequest
): Promise<IScheduledTriggerPreview> => {
  const url = `/v1/business/${slug}/workflows/scheduled_trigger_preview/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData
  })
  return data
}

export const fetchWorkflowNodeTypes: QueryFunction<
  WorkflowListProps,
  IWorkflowNodeTypeOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/workflow_node_types/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchWorkflowFilterTypes: QueryFunction<
  WorkflowListProps,
  IWorkflowFilterTypeOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/workflow_filter_types/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchWorkflows: QueryFunction<
  WorkflowListProps,
  PaginatedResponse<IWorkflow>
> = async ({ slug }, params) => {
  const url = `/v1/business/${slug}/workflows/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const fetchWorkflow: QueryFunction<WorkflowInstanceProps, IWorkflow> = async ({
  slug,
  workflowId
}) => {
  const url = `/v1/business/${slug}/workflows/${workflowId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchWorkflowRuns: QueryFunction<
  WorkflowInstanceProps,
  PaginatedResponse<IWorkflowRun>
> = async ({ slug, workflowId }, queryParams) => {
  const url = `/v1/business/${slug}/workflows/${workflowId}/runs/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: queryParams
  })
  return data
}

export const fetchWorkflowRun: QueryFunction<WorkflowRunDetailProps, IWorkflowRunDetail> = async ({
  slug,
  workflowId,
  runId
}) => {
  const url = `/v1/business/${slug}/workflows/${workflowId}/runs/${runId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const cancelWorkflowRun: MutationFunction<WorkflowRunDetailProps, void> = async ({
  slug,
  workflowId,
  runId
}) => {
  const url = `/v1/business/${slug}/workflows/${workflowId}/runs/${runId}/cancel/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const createWorkflow: MutationFunction<WorkflowListProps, IWorkflow> = async (
  { slug },
  requestData
) => {
  const url = `/v1/business/${slug}/workflows/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const updateWorkflow: MutationFunction<WorkflowInstanceProps, IWorkflow> = async (
  { slug, workflowId },
  requestData
) => {
  const url = `/v1/business/${slug}/workflows/${workflowId}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteWorkflow: MutationFunction<WorkflowInstanceProps, void> = async ({
  slug,
  workflowId
}) => {
  const url = `/v1/business/${slug}/workflows/${workflowId}/`
  const { status } = await network.httpClient.request({
    url,
    method: 'DELETE',
    validateStatus: () => true
  })
  if (status >= 400) {
    throw new Error('Failed to delete workflow')
  }
}
